import { createStore } from '@xstate/store';
import type { UIContext, ActiveDrag } from './types';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 20.0;
const ZOOM_STEP = 0.25;

const initialContext: UIContext = {
  activeDrag: null,
  dragOverColumn: null,
  selectedTaskId: null,
  selectedSkillId: null,
  zoomLevel: 1.0,
  showImportModal: false,
  importText: '',
  importError: null,
  contextMenu: null,
  compactView: false,
};

export const uiStore = createStore({
  context: initialContext,

  on: {
    'drag.start': (ctx, event: { drag: ActiveDrag }) => {
      console.log('[uiStore] drag.start');
      return {
        ...ctx,
        activeDrag: event.drag,
      };
    },

    'drag.move': (ctx, event: { overColumn: string | null }) => {
      // CRITICAL: Return same reference if nothing changed to prevent infinite re-renders
      if (ctx.dragOverColumn === event.overColumn) {
        return ctx;
      }
      console.log('[uiStore] drag.move changed to:', event.overColumn);
      return {
        ...ctx,
        dragOverColumn: event.overColumn,
      };
    },

    'drag.end': (ctx) => ({
      ...ctx,
      activeDrag: null,
      dragOverColumn: null,
    }),

    'task.select': (ctx, event: { taskId: string | null }) => {
      // Return same reference if nothing changed
      if (ctx.selectedTaskId === event.taskId && ctx.contextMenu === null) {
        return ctx;
      }
      return {
        ...ctx,
        selectedTaskId: event.taskId,
        selectedSkillId: null, // Close skill panel when task is selected
        contextMenu: null, // Close context menu on selection change
      };
    },

    'skill.select': (ctx, event: { skillId: string | null }) => {
      // Return same reference if nothing changed
      if (ctx.selectedSkillId === event.skillId) {
        return ctx;
      }
      return {
        ...ctx,
        selectedSkillId: event.skillId,
        selectedTaskId: null, // Close task panel when skill is selected
        contextMenu: null,
      };
    },

    'zoom.set': (ctx, event: { level: number }) => ({
      ...ctx,
      zoomLevel: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, event.level)),
    }),

    'zoom.in': (ctx) => ({
      ...ctx,
      zoomLevel: Math.min(ZOOM_MAX, ctx.zoomLevel + ZOOM_STEP),
    }),

    'zoom.out': (ctx) => ({
      ...ctx,
      zoomLevel: Math.max(ZOOM_MIN, ctx.zoomLevel - ZOOM_STEP),
    }),

    'zoom.reset': (ctx) => ({
      ...ctx,
      zoomLevel: 1.0,
    }),

    'modal.import.open': (ctx) => ({
      ...ctx,
      showImportModal: true,
      importText: '',
      importError: null,
    }),

    'modal.import.close': (ctx) => ({
      ...ctx,
      showImportModal: false,
      importText: '',
      importError: null,
    }),

    'modal.import.setText': (ctx, event: { text: string }) => ({
      ...ctx,
      importText: event.text,
      importError: null,
    }),

    'modal.import.setError': (ctx, event: { error: string | null }) => ({
      ...ctx,
      importError: event.error,
    }),

    'contextMenu.open': (ctx, event: { x: number; y: number; taskId: string }) => ({
      ...ctx,
      contextMenu: { x: event.x, y: event.y, taskId: event.taskId },
    }),

    'contextMenu.close': (ctx) => {
      // Return same reference if already closed
      if (ctx.contextMenu === null) {
        return ctx;
      }
      return {
        ...ctx,
        contextMenu: null,
      };
    },

    'compactView.toggle': (ctx) => ({
      ...ctx,
      compactView: !ctx.compactView,
    }),

    'ui.reset': () => initialContext,
  },
});

// Debug: track subscription notifications
let uiStoreSubCount = 0;
uiStore.subscribe(() => {
  uiStoreSubCount++;
  if (uiStoreSubCount % 100 === 1) {
    console.log('[uiStore.subscribe] notification #', uiStoreSubCount);
  }
});

export type UIStore = typeof uiStore;
